import { reset, getGroups, setGroups } from "./grouping/state";
import {
	addGroup,
	removeGroup,
	updateGroupPositions,
	mergeAdjacentGroups,
} from "./grouping/structure";
import {
	isInactiveSheet,
	getSheetGroups,
	setSheetGroups,
	refreshGroupView,
	withGroupSync,
	mapGroups,
} from "./grouping/helpers";
import {
	restoreHiddenState,
	syncAndRefreshGroups,
	syncOnHideShow,
	initHiddenStateFromGroups,
} from "./grouping/visibility";
import { getSheet } from "../sheets";

export function init(view) {
	_initGroupViewHandlers(view);
	reset(view);

	view.attachEvent("onReset", () => reset(view));

	view.attachEvent("onCommand", action => {
		if (action.id === "group" || action.id === "ungroup") {
			if (action.id === "group") addGroup(view, action.group);
			else removeGroup(view, action.group);
		}
	});

	// add/delete
	view.attachEvent("onAction", (action, p) => {
		if (action === "before-grid-change")
			updateGroupPositions(view, p.name, p.inc, p.data, p.start);
	});

	// sync groups when filter changes (filtered rows behave like hidden rows)
	view.$$("cells").attachEvent("onAfterFilter", () => {
		if (!view._groupSyncing) {
			syncAndRefreshGroups(view, "row");
		}
	});

	// hide/show
	view.attachEvent("onColumnOperation", (action, start, end, page) => {
		if (action === "hide" || action === "show")
			syncOnHideShow(view, "column", page);
	});

	view.attachEvent("onRowOperation", (action, start, end, page) => {
		if (action === "hide" || action === "show")
			syncOnHideShow(view, "row", page);
	});

	view.attachEvent(
		"onUndo",
		(action, row, column, value, direction, extra) => {
			_onGroupUndo(view, action, value, direction, extra);
		}
	);
}

export function serialize(view, data) {
	if (view._row_groups.length || view._col_groups.length) {
		data.table.groups = {};
	}
	if (view._row_groups.length)
		data.table.groups.row = mapGroups(view._row_groups);
	if (view._col_groups.length)
		data.table.groups.column = mapGroups(view._col_groups);
}

export function load(view, data) {
	reset(view);

	if (data.table && data.table.groups) {
		if (data.table.groups.column)
			setGroups(
				view,
				"column",
				_normalizeGroups(webix.copy(data.table.groups.column))
			);
		if (data.table.groups.row)
			setGroups(
				view,
				"row",
				_normalizeGroups(webix.copy(data.table.groups.row))
			);
	}

	initHiddenStateFromGroups(view, "column");
	initHiddenStateFromGroups(view, "row");
	refreshGroupView(view, "column");
	refreshGroupView(view, "row");
}

function _initGroupViewHandlers(view) {
	const colGroups = view.$$("columnGroups");
	const rowGroups = view.$$("rowGroups");
	const intersection = view.$$("groupIntersection");

	if (colGroups) colGroups.ready();
	if (rowGroups) rowGroups.ready();
	if (intersection) intersection.ready();
}

function _onGroupUndo(view, action, value, direction, extra) {
	if (action && (action.id === "group" || action.id === "ungroup")) {
		if (extra) {
			const groups = direction ? extra.newValue : extra.value;
			if (isInactiveSheet(view, extra.page)) {
				const sheet = getSheet(view, extra.page);
				if (sheet)
					setSheetGroups(sheet, action.group, webix.copy(groups));
			} else {
				setGroups(view, action.group, webix.copy(groups));
				refreshGroupView(view, action.group);
			}
		}
	}

	if (action === "open-group" || action === "close-group") {
		if (isInactiveSheet(view, value.page)) {
			const sheet = getSheet(view, value.page);
			if (sheet) {
				const groups = webix.copy(getSheetGroups(sheet, value.type));
				_applyStatesById(groups, value.states);
				setSheetGroups(sheet, value.type, groups);
			}
		} else {
			withGroupSync(
				view,
				() => {
					_applyStatesById(getGroups(view, value.type), value.states);
					restoreHiddenState(
						view,
						value.type,
						value.hiddenState,
						value.start,
						value.end
					);
				},
				false
			);
			refreshGroupView(view, value.type);
		}
	}

	if (
		(action.group === "row" || action.group === "column") &&
		(action.id === "hide" || action.id === "show")
	) {
		syncAndRefreshGroups(view, action.group);
	}
}

function _normalizeGroups(groups) {
	const valid = [];

	for (let i = 0; i < groups.length; i++) {
		const g = groups[i];

		// start is required and must be a non-negative number
		if (typeof g.start !== "number" || g.start < 0) continue;

		const rawEnd = typeof g.end === "number" ? g.end : g.start;
		const start = Math.min(g.start, rawEnd);
		const end = Math.max(g.start, rawEnd);
		const level =
			typeof g.level === "number" && g.level >= 0
				? Math.min(g.level, 8)
				: 0;

		valid.push({
			id: g.id || webix.uid(),
			start,
			end,
			level,
			open: g.open !== false,
		});
	}

	if (!valid.length) return valid;

	// deduplicate exact (start, end, level) triples — merge open states
	valid.sort(
		(a, b) => a.level - b.level || a.start - b.start || b.end - a.end
	);
	for (let i = valid.length - 1; i > 0; i--) {
		const c = valid[i],
			p = valid[i - 1];
		if (c.start === p.start && c.end === p.end && c.level === p.level) {
			p.open = p.open || c.open;
			valid.splice(i, 1);
		}
	}

	// remove groups fully contained within a wider group at the same level
	let maxEnd = -1;
	let curLevel = -1;
	for (let i = 0; i < valid.length; ) {
		const g = valid[i];
		if (g.level !== curLevel) {
			curLevel = g.level;
			maxEnd = -1;
		}
		if (g.end <= maxEnd) {
			valid.splice(i, 1);
		} else {
			maxEnd = g.end;
			i++;
		}
	}

	mergeAdjacentGroups(valid, true);
	return valid;
}

function _applyStatesById(groups, states) {
	const byId = new Map(groups.map(g => [g.id, g]));
	for (let i = 0; i < states.length; i++) {
		const g = byId.get(states[i].id);
		if (g) g.open = states[i].open;
	}
}
